// Port of data_centrifuge_view.coffee#renderComponentModel (:571-699), MINUS the
// Backbone @componentModel.set and the cross-feature `callback` (Controller.jsx wires
// onChange/onCallback/onFetch/onFilter directly to controller methods instead).
//
// Returns { data, job } — the same shape the legacy componentModel carried.
// Controller.jsx maps `data` into the Chart Component's `model` prop (mirroring
// data_centrifuge/component.coffee) and into the Responses `data` prop. Keeps the
// _.isEqual in-place diffing so nested refs stay stable across poll ticks.

import { isEqual } from 'lodash'

import { VECTORS_RAW, VECTORS_NAMES, getParentGroup } from '@aytm/app/stats/page/data-centrifuge/lib/vectors'
import { can, CAPABILITY } from '@aytm/app/stats/page/data-quality-report/lib/tiers'
import {
  isAutoEnabledLikeVector,
  isDcQuarantineEnabled
} from '@aytm/app/stats/page/data-quality-report/controller/vector-predicates'

export const buildComponentProps = (componentProps, state) => {
  const {
    tier,
    data: rawData,
    jobData,
    componentData,
    disabledVectors,
    filter,
    suspicionLevel,
    features,
    surveyConfig
  } = state

  const props = componentProps || { data: {}, job: {} }
  props.job = jobData

  const data = props.data

  const vectors = componentData.vectorIds.map((v, i) => ({ id: v, text: componentData.vectorNames[i] }))
  if (!isEqual(vectors, data.vectors)) data.vectors = vectors
  if (!isEqual(disabledVectors, data.disabledVectors)) data.disabledVectors = disabledVectors

  // Vectors that ran successfully but produced no scores — they live in disabledVectors
  // so chart/scoring skip them, but the sidebar paints them enabled. Pass through so
  // React can apply the same override.
  const autoEnabledLikeVectors = VECTORS_RAW.reduce((acc, id) => {
    if (isAutoEnabledLikeVector(componentData, id)) acc.push({ id, text: VECTORS_NAMES[id] })
    return acc
  }, [])
  if (!isEqual(autoEnabledLikeVectors, data.autoEnabledLikeVectors))
    data.autoEnabledLikeVectors = autoEnabledLikeVectors

  data.filter = filter
  data.suspicionLevel = suspicionLevel

  if (!isEqual(componentData.responses, data.responses)) data.responses = componentData.responses

  if (!isEqual(componentData.scores, data.scores)) data.scores = componentData.scores

  // Hoisted lookups + filter helper — used by both filter passes below.
  const quarantineLookup = {}
  componentData.quarantineUIDs.forEach(uid => {
    quarantineLookup[uid] = true
  })

  const disabledVectorsLookup = {}
  disabledVectors.forEach(id => {
    disabledVectorsLookup[id] = true
  })

  const scoresByUid = {}
  componentData.scores.forEach(s => {
    scoresByUid[s[0]] = s
  })

  const filterMatch = uid => {
    const isQuarantined = quarantineLookup[uid]
    return filter === 'all' || (!isQuarantined && filter !== 'quarantined') || (isQuarantined && filter !== 'flagged')
  }

  const vectorIds = componentData.vectorIds

  // Single walk: builds the filtered list and the contributing-UID set together.
  const scoresFiltered = []
  const contributingUidsLookup = {}
  componentData.scores.forEach(s => {
    const uid = s[0]
    if (suspicionLevel > componentData.suspicionScores[uid]) return
    if (!filterMatch(uid)) return

    let contributed = false
    for (let vi = 0; vi < vectorIds.length; vi++) {
      const vectorId = vectorIds[vi]
      if (disabledVectorsLookup[vectorId]) continue
      if (s[vi + 1] > 0) {
        contributed = true
        break
      }
    }

    if (contributed) {
      scoresFiltered.push(s)
      contributingUidsLookup[uid] = true
    }
  })

  data.scoresFiltered = scoresFiltered

  data.advisoryUIDs = componentData.advisoryUIDs
  data.advisoryOnlyVectorIds = componentData.advisoryOnlyVectorIds || []
  data.advisoryScores = componentData.advisoryScores

  // Advisory rows are not on the threshold scale, so the suspicion-level gate does not apply —
  // only the filter tab and whether the vector that flagged them is still checked. This is the
  // single set the Advisory response group and the summary table's Advisory row read, so the tier
  // gate belongs here rather than at each surface.
  data.advisoryFiltered = can(tier, CAPABILITY.SHOW_ADVISORY)
    ? componentData.advisoryUIDs.filter(uid => {
        if (!filterMatch(uid)) return false
        return Object.keys(componentData.advisoryScores[uid] || {}).some(
          leafId => !disabledVectorsLookup[leafId] && !disabledVectorsLookup[getParentGroup(leafId) || leafId]
        )
      })
    : []

  if (!isEqual(componentData.answerScores, data.answerScores)) data.answerScores = componentData.answerScores
  if (!isEqual(componentData.suspicionScores, data.suspicionScores))
    data.suspicionScores = componentData.suspicionScores
  if (!isEqual(componentData.excludedScores, data.excludedScores)) data.excludedScores = componentData.excludedScores

  data.quarantineUIDs = componentData.quarantineUIDs
  data.quarantineUIDsFiltered = componentData.quarantineUIDs.filter(uid => {
    const s = scoresByUid[uid]
    if (!s) return false

    return (
      ['all', 'quarantined'].includes(filter) &&
      suspicionLevel <= componentData.suspicionScores[s[0]] &&
      contributingUidsLookup[uid]
    )
  })

  if (Object.hasOwn(componentData, 'dcLastRunTs')) data.dcLastRunTs = componentData.dcLastRunTs
  else delete data.dcLastRunTs
  if (Object.hasOwn(componentData, 'dcAnalyzedCount')) data.dcAnalyzedCount = componentData.dcAnalyzedCount
  else delete data.dcAnalyzedCount
  if (Object.hasOwn(componentData, 'notAnalyzedCount')) data.notAnalyzedCount = componentData.notAnalyzedCount
  else delete data.notAnalyzedCount

  data.dcQuarantineEnabled = isDcQuarantineEnabled(componentData, rawData, features, surveyConfig)

  return props
}
